import { FacilityUnitActions, FacilityUnitKey, FacilityUnitReducer } from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/UnitTab/Store/FacilityUnit.redux';
import { selectUnitsGridData } from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/UnitTab/Store/FacilityUnit.selector';
import { selectUnitId } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import AddIcon from '@mui/icons-material/Add';
import { Card, CardContent, CircularProgress, Grid, IconButton, Paper, Skeleton, Typography } from '@mui/material';
import ExistingActiveUnitIcon from 'app/assets/images/Order/ActiveUnit.svg';
import ExistingUnitIcon from 'app/assets/images/Order/Unit.svg';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useInjectReducer } from 'redux-injectors';
import { makeStyles } from 'tss-react/mui';
import { UnitList } from './UnitList';

const useStyles = makeStyles<{ isUnitAvailable: boolean }>()((theme, { isUnitAvailable }) => ({
  cardContainer: {
    minHeight: '200px',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderRadius: '5px',
  },
  cardHover: {
    '&:hover': {
      boxShadow: '0px 3px 6px #00000014',
      border: `1px solid ${theme.palette.components.card.cardContent.hoverBorderColor}`,
      color: `${theme.palette.components.card.cardContent.hoverColor} !important`,
      backgroundColor: theme.palette.components.card.cardContent.hoverBackgroundColor,
    },
  },
  unitCard: {
    cursor: 'pointer',
    color: isUnitAvailable ? theme.palette.system.neutralGray : theme.palette.components.card.cardContent.hoverColor,
    border: `1px solid ${
      isUnitAvailable ? theme.palette.system.lightGrey : theme.palette.components.card.cardContent.hoverBorderColor
    }`,
  },
  activeHoverCard: {
    color: theme.palette.components.card.cardContent.hoverColor,
    border: `1px solid ${theme.palette.components.card.cardContent.hoverBorderColor}`,
  },
  activeCard: {
    backgroundColor: theme.palette.system.skyBlue,
  },
  inactiveCard: {
    color: theme.palette.system.lightGrey,
    cursor: 'default',
    opacity: 0.5,
  },
  validationLoader: {
    left: '50%',
    top: '50%',
    position: 'absolute',
  },
  BoxContainer: {
    padding: '16px 16px 24px 16px',
    justifyContent: "space-between !important"
  },
}));

export const UnitSelection = () => {
  useInjectReducer({ key: FacilityUnitKey, reducer: FacilityUnitReducer });
  
  const { t } = useTranslation();
  const unitsFlattenedData = useSelector(selectUnitsGridData);
  const selectedUnitId = useSelector(selectUnitId);
  const history = useNavigate();
  const dispatch = useDispatch();
  const isUnitAvailable = unitsFlattenedData?.length > 0;
  const { id: facilityId } = useParams<{ id: string }>();
  const { promiseInProgress: isCallingAPI } = usePromiseTracker({
    area: 'get-facility-details-with-filters',
    delay: 0,
  });
  ;
  const { classes, cx } = useStyles({ isUnitAvailable });

  const createNewUnit = () => {
    dispatch(FacilityUnitActions.setCreateNewUnitFromOrder(false));
    dispatch(FacilityUnitActions.setCreateNewUnit(true));
    history(`/facility/${facilityId}/newUnit`);
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" fontWeight={'normal'}>
          {' '}
          {t('order.newOrderCreation.unitCreation.header')}
        </Typography>
      </Grid>
      {isCallingAPI ? (
        <Grid item xs={12} style={{ minHeight: '10rem' }}>
          <Skeleton variant="rectangular" className={classes.cardContainer} />
        </Grid>
      ) : (
        <Grid container item spacing={4}>
          <Grid item xs={1.75} alignItems={'center'} pt={'18px !important'}>
            <Card onClick={createNewUnit}>
              <CardContent className={`${classes.cardContainer} ${classes.unitCard} ${classes.cardHover}`}>
                <AddIcon
                  id="add-icon-unit"
                  fontSize="small"
                  sx={{
                    fontSize: '95px',
                    stroke: theme => theme.palette.components.icons.primary.color,
                    strokeWidth: 1,
                    marginTop: '17px',
                  }}
                />
                <Typography sx={{ paddingTop: '24px' }} variant="body2" textTransform={'uppercase'}>
                  {t('order.facility.facilityHeader.newUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={1.75} pt={'18px !important'}>
            <Paper
              className={cx(classes.cardContainer,classes.BoxContainer, {
                [classes.cardHover]: isUnitAvailable,
                [classes.activeHoverCard]: isUnitAvailable,
                [classes.inactiveCard]: !isUnitAvailable,
                [classes.activeCard]: !isNullOrUndefined(selectedUnitId),
              })}
            >
              <IconButton
                color="primary"
                type="button"
                disableTouchRipple
                disableFocusRipple
                disableRipple
                size="large"
                sx={{ marginTop: '50px' }}
              >
                <img
                  src={isUnitAvailable ? ExistingActiveUnitIcon : ExistingUnitIcon}
                  alt="Existing Unit"
                  style={{ cursor: 'default' }}
                />
              </IconButton>
              <Typography sx={{ pt: '12px' }} gutterBottom variant="body2" textTransform={'uppercase'}>
                {t('order.newOrderCreation.unitCreation.selectExistingUnit')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={8.5} pt={'18px !important'}>
            <UnitList />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
